<template>
	<article class="movie-card-wrapper pt-3 pb-3" @click="() => $emit('onPosterClickOut')">
		<div class="movie-card">
			<PictureComp
				v-if="posterSRImage"
				:alt="campaignNode?.content.title"
				:sponsoredRecImages="posterSRImage"
				class="movie-poster"
				image-profile="poster"
				@imageError="src => $emit('imageError', src)"
			/>
			<div class="movie-info" v-if="!hideContent">
				<h2 class="movie-title">{{ promotionalTitle }}</h2>
				<p class="movie-year">{{ releaseYear }}</p>
			</div>
			<slot name="action-items" />
		</div>
	</article>
</template>

<script setup lang="ts">
import { SnowplowContext } from '@/helpers/tracking/providers';
import { PosterSponsoredRecImages } from '@/interfaces/components/poster/PosterGraphql';
import { SponsoredAdFragment } from '@/features/sponsored-recommendations/graphql/fragments/SponsoredAd.fragment';
import { computed } from 'vue';
import { useCampaignInfo } from '@/features/sponsored-recommendations/composables/useSendBidId';
import { Maybe } from '@/@types/graphql-types';
import PictureComp from '@/components/picture/PictureComp.vue';

const props = withDefaults(
	defineProps<{
		sponsoredAd: NonNullable<SponsoredAdFragment>;
		lazy?: boolean;
		additionalContexts?: SnowplowContext[];
		hideContent?: boolean;
	}>(),
	{
		lazy: true,
		additionalContexts: () => [],
	}
);

defineEmits<{
	(e: 'onPosterClickOut'): void;
	(e: 'imageError', src: Maybe<string>): void;
}>();

const { jwConfig } = useRuntimeConfig().public;

const { campaignNode, promotionalTitle, releaseYear } = useCampaignInfo(() => props.sponsoredAd.campaign);

const posterSRImage = computed<PosterSponsoredRecImages | null>(() => {
	let posterUrl = props.sponsoredAd.campaign?.posterOverride || campaignNode?.value.content.posterUrl;
	if (!posterUrl) return null;

	if (posterUrl.startsWith('/')) {
		posterUrl = `${jwConfig.IMAGESCALER_URL}${posterUrl.replace('{profile}', 's332')}`;
	}

	return {
		imageUrl: posterUrl,
		sourceSets: [
			{
				srcSet: posterUrl.replace(/\.{?\w+}?$/, '.avif'),
				imageProp: {
					extension: '.avif',
					type: 'image/avif',
				},
				sourceProp: {
					viewport: 'desktop',
				},
				media: null,
			},
		],
	};
});
</script>

<style scoped lang="scss">
.movie-card-wrapper {
	display: flex;
	align-items: center;
	justify-content: flex-start;
	gap: 16px;
	padding-left: 16px;
	padding-right: 16px;
	font-family: Lato, -apple-system, Roboto, Helvetica, sans-serif;
	letter-spacing: 0.24px;

	@media (max-width: 640px) {
		padding-left: 8px;
		padding-right: 8px;
	}
}

.movie-card {
	display: flex;
	align-items: center;
	justify-content: space-between;
	width: 100%;
	padding: 8px;
	gap: 12px;
	border-radius: 8px;
	border: 1px solid var(--border-border-high-active, #fbc500);
	background-color: #101720;
}

.movie-poster {
	width: 48px;
	min-width: 48px;
	aspect-ratio: 0.67;
	object-fit: cover;
	border-radius: 4px;
	flex-shrink: 0;

	v::deep(.picture-comp__img) {
		border-radius: 4px;
	}

	@media (max-width: 640px) {
		width: 40px;
		min-width: 40px;
	}
}

.movie-info {
	display: flex;
	flex-direction: column;
	justify-content: center;
	flex: 1;
	white-space: normal;
	overflow: hidden;
	text-overflow: ellipsis;
	min-width: 0;

	@media (max-width: 640px) {
		font-size: 14px;
	}
}

.movie-title {
	color: #d2d3d5;
	font-size: 16px;
	font-weight: 400;
	margin: 0;

	overflow: hidden;
	text-overflow: ellipsis;
	display: -webkit-box;
	line-clamp: 2;
	-webkit-line-clamp: 2;
	-webkit-box-orient: vertical;

	@media (max-width: 640px) {
		font-size: 14px;
	}
}

.movie-year {
	color: #909398;
	font-size: 14px;
	line-height: 1;
	margin-top: 4px;

	@media (max-width: 640px) {
		font-size: 12px;
	}
}
</style>
